import { useCallback, useEffect, useRef } from 'react';
import { startPlay, recordWave, recordMood, endPlay } from '../utils/playApi.js';

export function usePlayTelemetry({ onSession, getSnapshot } = {}) {
  const sessionIdRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const onSessionRef = useRef(onSession);
  const getSnapshotRef = useRef(getSnapshot);
  const pendingMoodRef = useRef(null);
  onSessionRef.current = onSession;
  getSnapshotRef.current = getSnapshot;

  const enqueue = useCallback((fn) => {
    queueRef.current = queueRef.current.then(fn).catch(() => {});
    return queueRef.current;
  }, []);

  const setPendingMood = useCallback((mood) => {
    pendingMoodRef.current = mood?.id != null ? mood : null;
  }, []);

  const handlePlayEvent = useCallback((evt) => {
    if (!evt?.type) return;

    if (evt.type === 'start') {
      enqueue(async () => {
        if (sessionIdRef.current) return;
        const data = await startPlay(evt);
        sessionIdRef.current = data?.session?.id || null;
        if (data?.session) onSessionRef.current?.(data.session);
      });
      return;
    }

    if (evt.type === 'wave-clear') {
      enqueue(async () => {
        const id = sessionIdRef.current;
        if (!id) return;
        await recordWave(id, evt);
      });
      return;
    }

    if (evt.type === 'end') {
      const id = sessionIdRef.current;
      if (!id) return;
      sessionIdRef.current = null;
      const mood = pendingMoodRef.current;
      pendingMoodRef.current = null;
      endPlay(id, {
        ...evt,
        ...(mood?.id != null && evt.moodId == null
          ? {
              moodId: mood.id,
              moodLabel: mood.label,
              moodAction: evt.exitAction === 'abandoned' ? 'abandoned' : (evt.moodAction || evt.action),
            }
          : {}),
      }, { keepalive: true });
    }
  }, [enqueue]);

  const submitMood = useCallback((mood, action, snapshot) => {
    const payload = {
      ...snapshot,
      moodId: mood?.id,
      moodLabel: mood?.label,
      action,
    };
    if (mood?.id == null) return;
    pendingMoodRef.current = null;

    if (action === 'return') {
      const id = sessionIdRef.current;
      if (!id) return;
      recordMood(id, payload, { keepalive: true });
      return;
    }

    enqueue(async () => {
      const id = sessionIdRef.current;
      if (!id) return;
      await recordMood(id, payload);
    });
  }, [enqueue]);

  useEffect(() => {
    const onHide = () => {
      const id = sessionIdRef.current;
      if (!id) return;
      sessionIdRef.current = null;
      const snapshot = getSnapshotRef.current?.() || {};
      const mood = pendingMoodRef.current;
      pendingMoodRef.current = null;
      endPlay(id, {
        ...snapshot,
        exitAction: 'abandoned',
        ...(mood?.id != null
          ? { moodId: mood.id, moodLabel: mood.label, moodAction: 'abandoned' }
          : {}),
      }, { keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  return { handlePlayEvent, submitMood, setPendingMood };
}

export default usePlayTelemetry;
